import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSetPasscode } from "@/hooks/usePasscode";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Lock } from "lucide-react";

const OBVIOUS = ["123456", "000000", "111111", "222222", "333333", "444444", "555555", "666666", "777777", "888888", "999999", "654321"];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** If true, modal cannot be closed by clicking outside or Escape */
  blocking?: boolean;
  /** Called after successful setup */
  onSuccess?: () => void;
}

export function PasscodeSetupModal({ open, onOpenChange, blocking, onSuccess }: Props) {
  const { role } = useAuth();
  const { toast } = useToast();
  const setPasscode = useSetPasscode();

  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [confirmDigits, setConfirmDigits] = useState(["", "", "", "", "", ""]);
  const [step, setStep] = useState<"enter" | "confirm">("enter");
  const [error, setError] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const confirmRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (open) {
      setDigits(["", "", "", "", "", ""]);
      setConfirmDigits(["", "", "", "", "", ""]);
      setStep("enter");
      setError("");
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [open]);

  const handleChange = (
    refs: React.MutableRefObject<(HTMLInputElement | null)[]>,
    setArr: React.Dispatch<React.SetStateAction<string[]>>,
    index: number,
    value: string
  ) => {
    if (!/^\d*$/.test(value)) return;
    setArr(prev => {
      const next = [...prev];
      next[index] = value.slice(-1);
      return next;
    });
    setError("");
    if (value && index < 5) {
      refs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    refs: React.MutableRefObject<(HTMLInputElement | null)[]>,
    arr: string[],
    index: number,
    e: React.KeyboardEvent
  ) => {
    if (e.key === "Backspace" && !arr[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  const handleProceedToConfirm = () => {
    const code = digits.join("");
    if (code.length !== 6) return;
    if (OBVIOUS.includes(code)) {
      setError("Please choose a less obvious passcode.");
      return;
    }
    setStep("confirm");
    setConfirmDigits(["", "", "", "", "", ""]);
    setTimeout(() => confirmRefs.current[0]?.focus(), 100);
  };

  const handleSubmit = async () => {
    const code = digits.join("");
    const confirm = confirmDigits.join("");
    if (code !== confirm) {
      setError("Passcodes do not match. Please try again.");
      setConfirmDigits(["", "", "", "", "", ""]);
      confirmRefs.current[0]?.focus();
      return;
    }

    try {
      await setPasscode.mutateAsync(code);
      toast({ title: "Passcode set. You are ready to go. ✅" });
      onOpenChange(false);
      onSuccess?.();
    } catch {
      setError("Something went wrong. Please try again.");
    }
  };

  const roleLabel = role === "super_admin" ? "Owner" : "Admin";
  const enterFilled = digits.every(d => d !== "");
  const confirmFilled = confirmDigits.every(d => d !== "");

  return (
    <Dialog
      open={open}
      onOpenChange={blocking ? undefined : onOpenChange}
    >
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={blocking ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={blocking ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Lock className="h-5 w-5 text-primary" />
            <DialogTitle className="font-display">
              {step === "enter" ? "Set up your security passcode" : "Confirm your passcode"}
            </DialogTitle>
          </div>
          <DialogDescription>
            {step === "enter"
              ? `As an ${roleLabel}, you can approve changes, record actual hours, and export sensitive data. A passcode protects these actions from being performed accidentally or by unauthorised users.`
              : "Enter the same 6-digit passcode again to confirm."}
          </DialogDescription>
        </DialogHeader>

        {step === "enter" && (
          <p className="text-xs text-muted-foreground">
            Your passcode is personal to you. Other users on your nursery's account have their own.
          </p>
        )}

        <div className="flex justify-center gap-2 py-4">
          {(step === "enter" ? digits : confirmDigits).map((digit, i) => (
            <Input
              key={`${step}-${i}`}
              ref={(el) => {
                (step === "enter" ? inputRefs : confirmRefs).current[i] = el;
              }}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) =>
                handleChange(
                  step === "enter" ? inputRefs : confirmRefs,
                  step === "enter" ? setDigits : setConfirmDigits,
                  i,
                  e.target.value
                )
              }
              onKeyDown={(e) =>
                handleKeyDown(
                  step === "enter" ? inputRefs : confirmRefs,
                  step === "enter" ? digits : confirmDigits,
                  i,
                  e
                )
              }
              className="w-11 h-13 text-center text-xl font-display font-bold"
            />
          ))}
        </div>

        {error && <p className="text-sm text-destructive text-center">{error}</p>}

        <div className="space-y-2">
          {step === "enter" ? (
            <Button
              className="w-full"
              onClick={handleProceedToConfirm}
              disabled={!enterFilled}
            >
              Continue
            </Button>
          ) : (
            <>
              <Button
                className="w-full"
                onClick={handleSubmit}
                disabled={!confirmFilled || setPasscode.isPending}
              >
                {setPasscode.isPending ? "Setting up…" : "Set my passcode"}
              </Button>
              <Button
                variant="ghost"
                className="w-full text-xs"
                onClick={() => {
                  setStep("enter");
                  setDigits(["", "", "", "", "", ""]);
                  setTimeout(() => inputRefs.current[0]?.focus(), 100);
                }}
              >
                ← Go back
              </Button>
            </>
          )}
          {blocking && step === "enter" && (
            <button
              type="button"
              className="w-full text-xs text-muted-foreground hover:text-foreground text-center py-1"
              onClick={() => onOpenChange(false)}
            >
              Skip for now, I'll do this later
              <span className="block text-[10px] text-muted-foreground">(you will be reminded on every login)</span>
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
